import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, Lock, PlayCircle, Sparkles, Clock, CheckCircle2, Filter, Search } from "lucide-react";

const TIER_LABEL = { basic: "Basic", pro: "Pro", premium: "Premium" };
const TIER_COLOR = { basic: "#64748B", pro: "#0EA5E9", premium: "#F59E0B" };

export default function Formazione() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | unlocked | locked

  useEffect(() => {
    api.get("/formazione/courses").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const courses = data?.courses || [];
  const categories = useMemo(() => {
    const s = new Set(courses.map((c) => c.category));
    return Array.from(s);
  }, [courses]);
  const [category, setCategory] = useState("");

  const filtered = useMemo(() => {
    let list = courses.slice();
    if (filter === "unlocked") list = list.filter((c) => c.unlocked);
    if (filter === "locked") list = list.filter((c) => !c.unlocked);
    if (category) list = list.filter((c) => c.category === category);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) =>
      c.title.toLowerCase().includes(q) || (c.subtitle || "").toLowerCase().includes(q)
    );
    return list;
  }, [courses, filter, category, query]);

  const unlockedCount = courses.filter((c) => c.unlocked).length;
  const lockedCount = courses.length - unlockedCount;

  return (
    <div data-testid="formazione-page" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
            <GraduationCap className="h-8 w-8 text-[color:var(--sp-brand)]" /> Formazione
          </h1>
          <p className="mt-1 text-sm text-[color:var(--sp-text-secondary)]">
            Corsi Salon Up per titolari di salone. Il tuo piano attuale: <strong>{TIER_LABEL[data?.tenant_tier] || "Basic"}</strong> · {unlockedCount} corsi disponibili, {lockedCount} da sbloccare.
          </p>
        </div>
      </header>

      {/* Toolbar filtri */}
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center" data-testid="formazione-toolbar">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input placeholder="Cerca corso…" data-testid="formazione-search"
            className="sp-input !pl-10 !py-1.5 text-sm"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          {[
            { k: "all", l: "Tutti" },
            { k: "unlocked", l: "Sbloccati" },
            { k: "locked", l: "Da sbloccare" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`formazione-filter-${f.k}`}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filter === f.k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
              {f.l}
            </button>
          ))}
        </div>
        {categories.length > 1 && (
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="sp-input !py-1 text-xs w-auto" data-testid="formazione-category">
            <option value="">Tutte le categorie</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="sp-card p-4 space-y-3 animate-pulse">
              <div className="h-40 bg-[color:var(--sp-subtle)] rounded-lg" />
              <div className="h-4 bg-[color:var(--sp-subtle)] rounded w-3/4" />
              <div className="h-3 bg-[color:var(--sp-subtle)] rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="sp-card p-10 text-center text-sm text-[color:var(--sp-text-muted)]">
          Nessun corso trovato con questi filtri.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      )}
    </div>
  );
}

function CourseCard({ course }) {
  const locked = !course.unlocked;
  return (
    <Link
      to={`/app/formazione/${course.id}`}
      data-testid={`course-card-${course.id}`}
      className="sp-card overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all"
    >
      {/* Cover */}
      <div className="relative h-40 overflow-hidden bg-gradient-to-br from-cyan-400 to-violet-500">
        {course.cover_url ? (
          <img src={course.cover_url} alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GraduationCap className="h-16 w-16 text-white/40" />
          </div>
        )}
        {locked ? (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white">
              <Lock className="h-8 w-8 mx-auto opacity-80" />
              <div className="text-xs font-bold mt-1 uppercase tracking-widest">
                Sblocca con {TIER_LABEL[course.required_tier]}
              </div>
              {course.price_eur ? (
                <div className="text-xs mt-1">€ {course.price_eur}</div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold uppercase tracking-widest">
            <CheckCircle2 className="h-3 w-3" /> Disponibile
          </div>
        )}
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white"
              style={{ background: TIER_COLOR[course.required_tier] }}>
          {TIER_LABEL[course.required_tier]}
        </span>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">{course.category}</div>
        <h3 className="font-display font-black text-lg mt-1 line-clamp-2">{course.title}</h3>
        {course.subtitle && (
          <p className="text-xs text-[color:var(--sp-text-secondary)] mt-1 line-clamp-2">{course.subtitle}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-[color:var(--sp-text-muted)] mt-3">
          <span className="inline-flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> {course.lessons_count} lezioni</span>
          {course.duration_min > 0 && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {Math.round(course.duration_min / 60)}h</span>
          )}
          <span className="ml-auto font-bold text-[color:var(--sp-text-secondary)]">{course.instructor}</span>
        </div>

        {!locked && course.lessons_count > 0 && (
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
              <div className="h-full transition-all" style={{
                width: `${course.progress_pct || 0}%`,
                background: course.progress_pct === 100 ? "#10B981" : "var(--sp-brand)",
              }} />
            </div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-1">
              {course.progress_pct === 100 ? (
                <><Sparkles className="h-3 w-3 text-emerald-500" /> Completato</>
              ) : (
                <>{course.progress_pct || 0}% completato</>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}
